/**
 * EditorialKpiRail — a horizontal KPI / stat rail bounded by hairline
 * top + bottom rules, the columns separated by thin vertical hairlines.
 *
 * Editorial reference: `.kpirail` / `.adq` in
 *   docs/design/round3-exec-summary.html (Exec D1) +
 *   docs/design/round3-intent-power.html (Power adequacy) +
 *   docs/design/round3-water-climate-env.html (Water/Climate/Env ledgers):
 *     border-top/bottom: 1px solid var(--hair-strong) ← roleColor(theme,'rule')
 *     each cell: mono small-caps key + a larger mono numeric value, with an
 *     optional trailing `<small>` unit in the muted ink.
 *
 * Used by:
 *   - Executive Summary (D1)            — Power / LCOE / Phases / COD rail
 *   - Power Availability (4A/4B)        — Firm / Need / Shortfall / ELCC
 *   - Water Resource (9A)               — Source / Draw / WRI stress / Cooling
 *   - Climate & Severe Weather (10A)    — Wet-bulb / CDD / Hurricane / Flood
 *   - Environmental (11A)               — Flood / Wetlands / Habitat
 *
 * Theme tokens read: 'rule' (bounding + divider rules → hairline), 'hair'
 * (the thinner per-cell divider), 'muted' (key + unit), 'ink' (value),
 * and the status roles ('warning'/'negative'/'positive') when a cell sets
 * a `tone`. Editorial mono font via --report-body-font. Numerics are
 * tabular (`fontVariantNumeric: 'tabular-nums'`) so the rail aligns.
 *
 * Pure / print-safe (no shadow, no animation). Cells whose `value` is
 * null/undefined/'' are DROPPED — the rail never fabricates a placeholder
 * number; an empty rail returns null so callers can omit it cleanly.
 *
 * LAYOUT IS FLEX, NOT GRID (Redhead compactness QA, 2026-07-02) — the paged
 * deck stylesheet de-fangs any `[style*="grid"]` element inside a flow section
 * to `display:block !important` (PaginatedReportView ~L888, the pagedjs
 * grid-collapse workaround), which stacked this rail ONE TILE PER ROW on the
 * view + PDF surfaces (first tile full-width, the rest as an indented
 * left-ruled block) and burned most of a page. Flex is the codebase-blessed
 * pagedjs-safe layout (see the deck comment: "EditorialRecommendations rows
 * already work around by using flex, not grid"). When every cell fits one row
 * (the house 1-col A4 measure) the render is geometry-identical to the former
 * `repeat(n, minmax(0,1fr))` grid; in a narrower measure the tiles WRAP into a
 * compact 2×2-style block (`flex-basis` floor per cell) instead of crushing.
 * Do not reintroduce the substring "grid" into the inline style — the deck
 * selector matches on it.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, type ReportColorRole } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export type EditorialKpiTone = 'default' | 'positive' | 'warning' | 'negative' | 'accent';

export interface EditorialKpiItem {
  /** Small-caps mono key, e.g. "Phase 1 LCOE". */
  key: React.ReactNode;
  /** Primary value. `null`/`undefined`/'' drops the cell (no fabrication). */
  value: React.ReactNode;
  /** Optional trailing unit (rendered small + muted, e.g. "MW", "/MWh"). */
  unit?: React.ReactNode;
  /** Retint the value ink for emphasis (e.g. a shortfall in 'negative'). */
  tone?: EditorialKpiTone;
  /**
   * Optional qualifier line under the value (small, faint ink), e.g. "of 30
   * state and national". Additive: omitted ⇒ the cell's DOM is unchanged.
   */
  note?: React.ReactNode;
}

export interface EditorialKpiRailProps {
  theme: ReportTheme;
  items: EditorialKpiItem[];
  /** Force a fixed column count; defaults to the rendered item count. */
  columns?: number;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

const TONE_ROLE: Record<EditorialKpiTone, ReportColorRole> = {
  default: 'ink',
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
  accent: 'accent',
};

/** A value is renderable when it is not null/undefined and (if a string)
 *  has non-whitespace content. Numbers/elements are always renderable. */
function isRenderableValue(value: React.ReactNode): boolean {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function EditorialKpiRail({
  theme,
  items,
  columns,
  id,
  className,
  style,
}: EditorialKpiRailProps): React.ReactElement | null {
  const rendered = items.filter((it) => isRenderableValue(it.value));
  if (rendered.length === 0) return null;

  const dense = theme.density === 'compact';
  const cols = columns ?? rendered.length;
  const rule = roleColor(theme, 'rule');
  const hair = roleColor(theme, 'hair');
  const muted = roleColor(theme, 'muted');

  // Per-cell flex basis: an explicit `columns` pins rows of exactly that many
  // cells (percent basis, grow shares the remainder equally — same geometry as
  // the former fixed grid); otherwise a 7.5rem floor lets every cell sit on one
  // row at the A4 measure and wrap ~2×2 in a narrow (half-column) measure
  // rather than crush.
  const cellBasis = columns ? `${Math.floor(10000 / columns) / 100}%` : '7.5rem';

  return (
    <div
      id={id}
      className={className}
      role="group"
      // House-style keep-whole hook (2026-06-29) — the paged deck targets this so a
      // KPI / metric rail never splits across a page seam (see PaginatedReportView).
      data-editorial-keep="true"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        borderTop: `1px solid ${rule}`,
        borderBottom: `1px solid ${rule}`,
        ...style,
      }}
    >
      {rendered.map((item, i) => {
        const valueInk = roleColor(theme, TONE_ROLE[item.tone ?? 'default']);
        // Row-start detection is DOM-index based (`i % cols`): exact for the
        // explicit-`columns` case and for the default single-row case. Under a
        // rare narrow-measure wrap a continuation row's first cell may carry a
        // leading hairline — cosmetically minor, and strictly better than the
        // stacked-tiles defect this replaced.
        const rowStart = i % cols === 0;
        return (
          <div
            key={i}
            style={{
              flex: `1 1 ${cellBasis}`,
              padding: dense ? '9px 12px 9px 0' : '12px 16px 12px 0',
              borderLeft: rowStart ? undefined : `1px solid ${hair}`,
              paddingLeft: rowStart ? 0 : dense ? 12 : 16,
            }}
          >
            <div
              style={{
                fontFamily: MONO,
                // 12px label/caption token (text-xs) — unified editorial label.
                fontSize: REPORT_TEXT_PX.label,
                fontWeight: 500,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: muted,
              }}
            >
              {item.key}
            </div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: dense ? 17 : 19,
                fontWeight: 500,
                letterSpacing: '-0.01em',
                color: valueInk,
                marginTop: 3,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {item.value}
              {item.unit != null && (
                <span style={{ fontSize: REPORT_TEXT_PX.annotation, color: muted, fontWeight: 400, marginLeft: 4 }}>
                  {item.unit}
                </span>
              )}
            </div>
            {item.note != null && item.note !== false && (
              <div
                style={{
                  fontFamily: MONO,
                  fontSize: REPORT_TEXT_PX.annotation,
                  color: roleColor(theme, 'faint'),
                  lineHeight: 1.35,
                  marginTop: 4,
                }}
              >
                {item.note}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
